function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Button } from '../Button';
import { Overlay } from '../Overlay';
import { Skeleton } from '../Skeleton';
import { Tooltip } from '../Tooltip';
import { isHeaderOverlayOpen } from '../Utils/helper';
import moduleStyles from '@dtsl/css-notification/dist/index.module.css';
function Notification(props) {
  const notificationRef = React.useRef(null);
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const {
    content,
    placement,
    headerData,
    footerData,
    defaultOpen,
    triggerData,
    tooltipData,
    panelClassName,
    contentItemClass,
    notificationIndicator,
    isMobile,
    isLoading,
    countOfSkeleton
  } = props;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const isOpenRef = React.useRef(isOpen);
  isOpenRef.current = isOpen;
  const {
    text: tooltipText,
    placement: tooltipPlacement = 'bottom',
    timeout = 200,
    offset: tooltipOffset = [0, 8]
  } = tooltipData || {};
  const {
    svg,
    to = '#',
    title = '',
    target = '_self',
    disabled = false,
    onClick = () => {}
  } = footerData;
  const handleDocumentClick = event => {
    if (notificationRef.current) {
      if (!notificationRef.current.contains(event.target)) {
        if (isMobile && isOverlayOpen) {
          setIsOverlayOpen(false);
        } else if (isOpen) {
          setIsOpen(false);
        }
      }
    }
  };
  useEffect(() => {
    document.addEventListener('click', handleDocumentClick);
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [isOpen, isOverlayOpen]);
  const onClickLink = event => {
    if (to === '#') {
      event.preventDefault();
      event.nativeEvent.stopImmediatePropagation();
    }
    setIsOverlayOpen(false);
    onClick(event);
  };
  const controlClasses = classNames({
    [moduleStyles['sib-notification__indicator']]: notificationIndicator,
    [triggerData.className]: !!triggerData.className
  });
  const nestedClasses = classNames({
    [moduleStyles['sib-notification__panel']]: true,
    [moduleStyles['placement-top-left']]: placement === 'topLeft',
    [moduleStyles['placement-top-right']]: placement === 'topRight',
    [moduleStyles['placement-bottom-right']]: placement === 'bottomRight',
    [moduleStyles['sib-notification__loading']]: isLoading,
    [panelClassName]: !!panelClassName
  });
  const overlayClasses = classNames({
    [moduleStyles['sib-notification__panel--mobile']]: true,
    [moduleStyles['sib-notification__panel--mobile-loading']]: isLoading
  });
  const handleOnClick = event => {
    const {
      onClick = () => {}
    } = triggerData;
    event.stopPropagation();
    event.preventDefault();
    setTooltipVisible(false);
    if (isMobile) {
      if (isHeaderOverlayOpen() && !isOverlayOpen) {
        setTimeout(() => {
          setIsOverlayOpen(!isOverlayOpen);
        }, 500);
      } else {
        setIsOverlayOpen(!isOverlayOpen);
      }
    } else {
      setIsOpen(!isOpen);
    }
    onClick(event);
  };
  const buttonHtml = tooltipText && !isMobile ? /*#__PURE__*/React.createElement(Tooltip, {
    text: tooltipText,
    offset: tooltipOffset,
    visible: tooltipVisible,
    placement: tooltipPlacement,
    onMouseEnter: () => {
      setTimeout(() => {
        if (!isOpenRef.current) {
          setTooltipVisible(true);
        }
      }, timeout);
    },
    onMouseLeave: () => {
      setTimeout(() => {
        setTooltipVisible(false);
      }, timeout);
    }
  }, /*#__PURE__*/React.createElement(Button, _extends({
    variant: "tertiary"
  }, triggerData, {
    className: `${triggerData.className ? triggerData.className : ''} ${controlClasses}`,
    dropdownOpen: isOpen,
    onClick: handleOnClick
  }))) : /*#__PURE__*/React.createElement(Button, _extends({
    variant: "tertiary"
  }, triggerData, {
    className: `${triggerData.className ? triggerData.className : ''} ${controlClasses}`,
    dropdownOpen: isOverlayOpen,
    onClick: handleOnClick,
    "data-is-panel-open": isOverlayOpen ? 'true' : 'false'
  }));
  const notificationPanelInnerHTML = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: moduleStyles['sib-notification__heading']
  }, headerData || ''), /*#__PURE__*/React.createElement("div", {
    className: moduleStyles['sib-notification__content'],
    tabIndex: "0",
    role: "article"
  }, content.map(item => /*#__PURE__*/React.createElement("div", {
    key: item,
    className: `${moduleStyles['sib-notification__content-item']} ${contentItemClass}`,
    dangerouslySetInnerHTML: {
      __html: item
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: moduleStyles['sib-notification__footer']
  }, /*#__PURE__*/React.createElement("a", _extends({
    key: title,
    target: target,
    className: `${moduleStyles['sib-notification__footer-link']} ${disabled ? moduleStyles['disabled'] : ''}`,
    disabled: disabled
  }, !disabled ? {
    onClick: onClickLink,
    href: to
  } : {}), /*#__PURE__*/React.createElement("span", {
    className: moduleStyles['sib-sidebar__label']
  }, svg ? /*#__PURE__*/React.cloneElement(svg, {
    className: 'sib-notification__footer-icon'
  }) : null, title))));
  const loadingHTML = /*#__PURE__*/React.createElement(Skeleton, {
    count: countOfSkeleton,
    height: 24
  });
  return /*#__PURE__*/React.createElement("div", {
    ref: notificationRef,
    className: moduleStyles['sib-notification']
  }, buttonHtml, !isMobile ? isOpen ? /*#__PURE__*/React.createElement("div", {
    className: nestedClasses,
    "aria-expanded": "true"
  }, !isLoading ? notificationPanelInnerHTML : loadingHTML) : null : /*#__PURE__*/React.createElement(Overlay, {
    isOpen: isOverlayOpen,
    className: overlayClasses
  }, !isLoading ? notificationPanelInnerHTML : loadingHTML));
}
export default Notification;
Notification.defaultProps = {
  content: [],
  tooltipData: {},
  footerData: {},
  triggerData: {},
  headerData: '',
  panelClassName: '',
  defaultOpen: false,
  contentItemClass: '',
  placement: 'bottomRight',
  notificationIndicator: false,
  isMobile: false,
  isLoading: false,
  countOfSkeleton: 10
};
Notification.propTypes = {
  content: PropTypes.array,
  tooltipData: PropTypes.object,
  defaultOpen: PropTypes.bool,
  footerData: PropTypes.object,
  headerData: PropTypes.string,
  placement: PropTypes.string,
  triggerData: PropTypes.object,
  panelClassName: PropTypes.string,
  contentItemClass: PropTypes.string,
  notificationIndicator: PropTypes.bool,
  isMobile: PropTypes.bool,
  isLoading: PropTypes.bool,
  countOfSkeleton: PropTypes.number
};